/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, {
	type CSSProperties,
	startTransition,
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { flushSync } from 'react-dom';

import { cssMap, jsx } from '@compiled/react';
import { bind } from 'bind-event-listener';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type MediaAboveMd from '@atlaskit/css/at-rules/media-above-md';
import mergeRefs from '@atlaskit/ds-lib/merge-refs';
import useStableRef from '@atlaskit/ds-lib/use-stable-ref';
import { OpenLayerObserverNamespaceProvider } from '@atlaskit/layering/open-layer-observer-namespace-provider';
import { useOpenLayerObserver } from '@atlaskit/layering/use-open-layer-observer';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/adapter/element-adapter';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- TODO: migrate to @atlaskit/primitives/compiled
import { token } from '@atlaskit/tokens';
import { hasOpenDialogOrPopover } from '@atlaskit/top-layer/page-surface/has-open-dialog-or-popover';

import { useSkipLinkInternal } from '../../../context/skip-links/use-skip-link-internal';
import { TopNavStartElement } from '../../../context/top-nav-start/top-nav-start-element';
import {
	UNSAFE_sideNavLayoutVar,
	type contentHeightWhenFixed,
	type contentInsetBlockStart,
	defaultMainMinWidth,
	type localSlotLayers,
	mainMinimumWidthVar,
	openLayerObserverSideNavNamespace,
	sideNavLiveWidthVar,
	sideNavPanelSplitterId,
	sideNavVar,
} from '../constants';
import { DangerouslyHoistCssVarToDocumentRoot } from '../dangerously-hoist-css-var-to-document-root';
import { DangerouslyHoistSlotSizes } from '../hoist-slot-sizes-context';
import { convertResizeBoundToPixels } from '../panel-splitter/convert-resize-bound-to-pixels';
import { PanelSplitterProvider } from '../panel-splitter/provider';
import type { ResizeBound, ResizeBounds } from '../panel-splitter/types';
import { resolveLayoutWidth } from '../resolve-layout-width';
import { gridRootChatFitId, gridRootId } from '../root';
import type { CommonSlotProps } from '../types';
import { useLayoutAreaSizing } from '../use-layout-area-sizing';
import { useLayoutId } from '../use-layout-id';
import { useResizingWidthCssVarOnRootElement } from '../use-resizing-width-css-var-on-root-element';
import { useSafeDefaultWidth } from '../use-safe-default-width';
import { bindOpenLayerChange } from './bind-open-layer-change';
import { sideNavFlyoutCloseDelayMs } from './flyout-close-delay-ms';
import { SetSideNavVisibilityState } from './set-side-nav-visibility-state';
import { SideNavToggleButtonElement } from './side-nav-toggle-button-element';
import { sideNavToggleTooltipKeyboardShortcut } from './side-nav-toggle-tooltip-keyboard-shortcut';
import { SideNavVisibilityState } from './side-nav-visibility-state';
import { useExpandSideNav } from './use-expand-side-nav';
import { useIsSideNavShortcutEnabled } from './use-is-side-nav-shortcut-enabled';
import { useSideNavRef } from './use-side-nav-ref';
import { useSideNavToggleKeyboardShortcut } from './use-side-nav-toggle-keyboard-shortcut';
import { useSideNavVisibility } from './use-side-nav-visibility';
import {
	type VisibilityCallback,
	useSideNavVisibilityCallbacks,
} from './use-side-nav-visibility-callbacks';
import { useToggleSideNav } from './use-toggle-side-nav';

const panelSplitterResizingVar = '--n_snvRsz';

const fallbackMinWidth: ResizeBound = '240px';

function getPixelCustomProperty(
	element: HTMLElement,
	propertyName: string,
	fallback: number,
): number {
	const raw = getComputedStyle(element).getPropertyValue(propertyName).trim();
	const value = Number.parseFloat(raw);
	return Number.isFinite(value)
		? resolveLayoutWidth(raw.endsWith('vw') ? `${value}vw` : value, window.innerWidth)
		: fallback;
}

/**
 * We are using JS to detect Firefox and disable animations, instead of using CSS, as Compiled currently does not merge duplicate
 * CSS at-rules when at-rules are nested: https://github.com/atlassian-labs/compiled/blob/e04a325915e1d13010205089e4915de0e53bc2d4/packages/css/src/plugins/merge-duplicate-at-rules.ts#L5
 * Avoiding nesting the `@supports` at-rule inside of `@media` means Compiled can remove duplicate styles from the generated CSS.
 */
const isFirefox: boolean =
	typeof navigator !== 'undefined' && navigator.userAgent.toLowerCase().indexOf('firefox') > -1;

const textEntryInputTypes = new Set([
	'text',
	'search',
	'email',
	'url',
	'tel',
	'password',
	'number',
]);

function isEditableContent({ element }: { element: Element }): boolean {
	if (element instanceof HTMLElement && typeof element.isContentEditable === 'boolean') {
		return element.isContentEditable;
	}
	// Fallback for DOM implementations without `isContentEditable`.
	const editingHost = element.closest('[contenteditable]');
	return editingHost !== null && editingHost.getAttribute('contenteditable') !== 'false';
}

/**
 * Returns `true` for an element that the user can type text into.
 */
function isTextEntryElement({ element }: { element: Element }): boolean {
	if (element instanceof HTMLInputElement) {
		return textEntryInputTypes.has(element.type) && !element.readOnly && !element.disabled;
	}
	if (element instanceof HTMLTextAreaElement) {
		return !element.readOnly && !element.disabled;
	}
	return isEditableContent({ element });
}

/**
 * Used when the `platform-dst-top-layer` gate is on.
 *
 * The flyout stays open while a layer opened from the side nav is open, or while the user
 * is typing in a text field inside the side nav (for example while renaming an item inline).
 */
function isFlyoutHeldOpen({ nav }: { nav: HTMLElement }): boolean {
	if (hasOpenDialogOrPopover({ within: nav })) {
		return true;
	}
	const activeElement = nav.ownerDocument.activeElement;
	return (
		activeElement !== null &&
		nav.contains(activeElement) &&
		isTextEntryElement({ element: activeElement })
	);
}

type FlyoutState =
	| { type: 'open' }
	| { type: 'is-dragging-from-flyout' }
	| { type: 'waiting-for-close'; abort: () => void }
	| { type: 'ready-to-close' }
	| { type: 'not-active' };

const styles = cssMap({
	root: {
		backgroundColor: token('elevation.surface.overlay'),
		boxShadow: token('elevation.shadow.overlay'),
		boxSizing: 'border-box',
		// Height is set so it takes up all of the available viewport space minus top bar + banner.
		// Since the side nav is always rendered ontop of other grid items across all viewports height is
		// always set.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		height:
			'calc(100vh - var(--n_bnrM, 0px) - var(--n_tNvM, 0px))' satisfies typeof contentHeightWhenFixed,
		// This sets the sticky point to be just below top bar + banner. It's needed to ensure the stick
		// point is exactly where this element is rendered to with no wiggle room. Unfortunately the CSS
		// spec for sticky doesn't support "stick to where I'm initially rendered" so we need to tell it.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		insetBlockStart:
			'calc(var(--n_bnrM, 0px) + var(--n_tNvM, 0px))' satisfies typeof contentInsetBlockStart,
		position: 'sticky',
		// Preserve the legacy compact width until the chat-panel layout rolls out.
		width: 'min(90%, 320px)',
		// On small viewports the side nav is displayed above other slots so we create a stacking context.
		// We keep the side nav with a stacking context always so it is rendered above main content.
		// This comes with a caveat that main is rendered underneath the side nav content so for any
		// menu dialogs rendered with "shouldRenderToParent" they could be cut off unintentionally.
		// Unfortunately this is the best of bad solutions.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		zIndex: 2 satisfies typeof localSlotLayers.sideNav,
		// Not required, but declaring explicitly because we really don't want a border at small sizes
		// Previously we had a transparent border to maintain width, but this unintentionally acted as padding
		borderInlineStart: 'none',
		borderInlineEnd: 'none',
		// Deliberately scoped to the exact inverse of the desktop `@media (min-width: 64rem)` rule
		// below, so the two can never both match. Unscoped, they would be atomic rules of equal
		// specificity, leaving the desktop winner up to insertion order — non-deterministic when
		// Compiled's extraction is disabled (local dev, streaming SSR). See MAGMA-4606.
		'@media not (min-width: 64rem)': {
			gridArea: 'main / aside / aside / aside',
		},
		'@media (min-width: 48rem)': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
			width: `var(${panelSplitterResizingVar}, var(${'--n_sNvw' satisfies typeof sideNavVar}))`,
		},
		'@media (min-width: 64rem)': {
			backgroundColor: token('elevation.surface'),
			boxShadow: 'initial',
			gridArea: 'side-nav',
			// We only want the border to be visible when it is not an overlay
			borderInlineEnd: `${token('border.width')} solid ${token('color.border')}`,
		},
	},
	chatPanelSizing: {
		// On compact viewports, the allocation system caps the overlay width.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		width: `var(${panelSplitterResizingVar}, var(${'--n_sNvw' satisfies typeof sideNavVar}))`,
	},
	managedOverlay: {
		'@media (min-width: 64rem)': {
			// Existing slots can leave too little room for an inline SideNav even on desktop.
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
			'&&': {
				gridArea: 'main',
				backgroundColor: token('elevation.surface.overlay'),
				boxShadow: token('elevation.shadow.overlay'),
				borderInlineEnd: 'none',
			},
		},
	},
	flyoutOpen: {
		'@media (min-width: 64rem)': {
			// These styles are in a media query to override the `styles.root` media query styles
			backgroundColor: token('elevation.surface.overlay'),
			boxShadow: token('elevation.shadow.overlay'),
			gridArea: 'main',
			// Hide the border for the flyout, because it has a shadow
			borderInlineEnd: 'none',
		},
		// Disabling animations for Firefox, as it doesn't support the close animation. See comment block in `shouldShowSidebarToggleAnimation` for more details.
		'@supports not (-moz-appearance: none)': {
			// Disabling animations if user has opted for reduced motion
			'@media (prefers-reduced-motion: no-preference)': {
				transitionProperty: 'transform, display',
				transitionDuration: '0.2s',
				transitionBehavior: 'allow-discrete',

				/**
				 * Because we're transitioning from display: none, we need to define the
				 * starting values for when the element is first displayed, so the
				 * transition animation knows where to start from.
				 */
				'@starting-style': {
					transform: 'translateX(-100%)',
				},
			},
		},
	},
	flyoutAnimateClosed: {
		display: 'none',
		'@media (min-width: 64rem)': {
			// These styles are in a media query to override the `styles.root` media query styles
			gridArea: 'main',
		},
		// Disabling animations for Firefox, as it doesn't support the close animation. See comment block in `shouldShowSidebarToggleAnimation` for more details.
		'@supports not (-moz-appearance: none)': {
			// Disabling animations if user has opted for reduced motion
			'@media (prefers-reduced-motion: no-preference)': {
				transitionProperty: 'transform, display',
				transitionDuration: '0.2s',
				transitionBehavior: 'allow-discrete',
				transform: 'translateX(-100%)',
			},
		},
	},
	flexContainer: {
		// This element controls the flex layout to position the slot elements correctly.
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
	},
	hiddenMobileAndDesktop: {
		display: 'none',
	},
	hiddenMobileOnly: {
		display: 'none',
		'@media (min-width: 64rem)': {
			display: 'initial',
		},
	},
	hiddenDesktopOnly: {
		'@media (min-width: 64rem)': {
			display: 'none',
		},
	},
});

/**
 * Styles for the side nav enter/exit animations when `platform-dst-motion-uplift-sidenav` is enabled.
 *
 * These use the `motion.sidenav.*` tokens (keyframe animations) instead of the `transform` transitions
 * with `@starting-style` used by `styles`.
 *
 * ⚠️ The `transition-duration` values below need to stay in sync with the duration baked into the tokens
 * (`motion.sidenav.enter.*` is 250ms, `motion.sidenav.exit.*` is 200ms). The `display` property is
 * transitioned (with `allow-discrete`) so the element stays rendered for the length of the exit animation —
 * if the transition is shorter than the animation, `display: none` applies early and the exit animation
 * never runs.
 */
const motionUpliftStyles = cssMap({
	animationRTLSupport: {
		// Used to support animations for right-to-left (RTL) languages/text direction. We need to flip the animation direction for RTL.
		// The motion tokens are directional (the keyframes translate towards a fixed physical side), so instead of flipping a
		// translate value we swap which animation token is used.
		'--enter-animation': token('motion.sidenav.enter.left'),
		'--exit-animation': token('motion.sidenav.exit.left'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
		"[dir='rtl'] &": {
			'--enter-animation': token('motion.sidenav.enter.right'),
			'--exit-animation': token('motion.sidenav.exit.right'),
		},
	},
	animationBaseStyles: {
		/**
		 * Disabling animations if user has opted for reduced motion
		 *
		 * ⚠️ Note: the `@media` query needs to be a top-level style to make sure Compiled orders the media queries correctly.
		 * Compiled currently only sorts top-level CSS rules:
		 * https://github.com/atlassian-labs/compiled/blob/master/packages/css/src/plugins/sort-atomic-style-sheet.ts#L39
		 */
		'@media (prefers-reduced-motion: no-preference)': {
			// The animation drives the movement, so only `display` needs to be transitioned.
			transitionProperty: 'display',
			transitionBehavior: 'allow-discrete',
			transitionDuration: token('motion.duration.medium'),
		},
	},
	expandAnimationMobile: {
		// These styles are not limited to "mobile" viewports, as they are not scoped to any media queries.
		// Desktop styles will need to override these if required.
		'@media (prefers-reduced-motion: no-preference)': {
			animation: 'var(--enter-animation)',
		},
	},
	collapseAnimationMobile: {
		// These styles are not limited to "mobile" viewports, as they are not scoped to any media queries.
		// Desktop styles will need to override these if required.
		'@media (prefers-reduced-motion: no-preference)': {
			gridArea: 'main',
			animation: 'var(--exit-animation)',
		},
	},
	flyoutOpen: {
		'@media (min-width: 64rem)': {
			// These styles are in a media query to override the `styles.root` media query styles
			backgroundColor: token('elevation.surface.overlay'),
			boxShadow: token('elevation.shadow.overlay'),
			gridArea: 'main',
			// Hide the border for the flyout, because it has a shadow
			borderInlineEnd: 'none',
		},
		// Disabling animations for Firefox, as it doesn't support the close animation. See comment block in `shouldShowSidebarToggleAnimation` for more details.
		'@supports not (-moz-appearance: none)': {
			// Disabling animations if user has opted for reduced motion
			'@media (prefers-reduced-motion: no-preference)': {
				transitionDuration: token('motion.duration.long'),
				transitionBehavior: 'allow-discrete',
				animation: 'var(--enter-animation)',
			},
		},
	},
	flyoutAnimateClosed: {
		display: 'none',
		'@media (min-width: 64rem)': {
			// These styles are in a media query to override the `styles.root` media query styles
			gridArea: 'main',
		},
		// Disabling animations for Firefox, as it doesn't support the close animation. See comment block in `shouldShowSidebarToggleAnimation` for more details.
		'@supports not (-moz-appearance: none)': {
			// Disabling animations if user has opted for reduced motion
			'@media (prefers-reduced-motion: no-preference)': {
				transitionDuration: token('motion.duration.medium'),
				transitionBehavior: 'allow-discrete',
				animation: 'var(--exit-animation)',
			},
		},
	},
	expandAnimationDesktop: {
		'@media (prefers-reduced-motion: no-preference) and (min-width: 64rem)': {
			// We need to override the mobile styles for desktop
			gridArea: 'side-nav',
			animation: 'var(--enter-animation)',
			transitionProperty: 'grid-area',
			transitionDuration: token('motion.duration.instant'),
			transitionDelay: '24ms', // Snaps main content when 60% of the side nav has entered
			'@starting-style': {
				gridArea: 'main',
			},
		},
	},
	collapseAnimationDesktop: {
		'@media (prefers-reduced-motion: no-preference) and (min-width: 64rem)': {
			gridArea: 'main',
			animation: 'var(--exit-animation)',
			transitionProperty: 'grid-area, display',
			transitionDuration: `${token('motion.duration.instant')}, ${token('motion.duration.medium')}`,
			transitionDelay: `19ms, ${token('motion.duration.instant')}`, // Snaps main content when 60% of the side nav has exited
		},
	},
});

type SideNavProps = CommonSlotProps & {
	/**
	 * The content of the layout area.
	 * Should include side nav layout areas as required: `SideNavHeader`, `SideNavBody`, `SideNavFooter`.
	 * Within these, you can use side nav menu items.
	 */
	children: React.ReactNode;
	/**
	 * The accessible name of the slot, announced by screen readers.
	 */
	label?: string;
	/**
	 * @deprecated
	 *
	 * This prop is being replaced by `defaultSideNavCollapsed` on the `Root` element,
	 * and will be removed in the future.
	 *
	 * ---
	 *
	 * Whether the side nav should be collapsed by default __on desktop screens__.
	 *
	 * It is always collapsed by default for mobile screens.
	 *
	 * This value is used when the side nav is first mounted, but you should continuously update your
	 * persisted state using the `onCollapse` and `onExpand` callbacks, to ensure it is up to date
	 * when the app is reloaded.
	 *
	 * __Note:__ If using this prop, ensure that it is also provided to the `SideNavToggleButton`.
	 * This is to ensure the state is in sync before post-SSR hydration.
	 */
	// eslint-disable-next-line @repo/internal/react/boolean-prop-naming-convention
	defaultCollapsed?: boolean;
	/**
	 * The default width of the side nav layout area.
	 *
	 * It should be an integer greater than or equal to the minimum width.
	 *
	 * It is only used when the side nav is first mounted, but you should continuously update your
	 * persisted state using the `onResizeEnd` callback of `PanelSplitter`, to ensure it is up to date
	 * when the app is reloaded.
	 */
	defaultWidth?: number;
	/**
	 * Minimum width used when fitting the side nav inline.
	 */
	minWidth?: ResizeBound;
	/**
	 * Optional maximum width for both inline and overlay resizing.
	 */
	maxWidth?: ResizeBound;
	/**
	 * Called when the side nav is expanded.
	 */
	onExpand?: VisibilityCallback;
	/**
	 * Called when the side nav is collapsed.
	 */
	onCollapse?: VisibilityCallback;

	/**
	 * Called when the side nav begins peeking / flyout.
	 */
	onPeekStart?: () => void;
	/**
	 * Called when the side nav stops peeking / flyout.
	 */
	onPeekEnd?: (args: { trigger: 'mouse-leave' | 'side-nav-expand' }) => void;

	/**
	 * Whether the side nav should be toggled in response to the built-in keyboard shortcut. Use this callback to
	 * conditionally disable the shortcut based on your own custom checks, e.g. if there is a legacy dialog open.
	 *
	 * This prop will do nothing if `isSideNavShortcutEnabled` on Root is not set to `true`, as the keyboard event
	 * listener is only binded if `isSideNavShortcutEnabled` is `true`.
	 *
	 * The shortcut key is `Ctrl` + `[`.
	 *
	 */
	canToggleWithShortcut?: () => boolean;
};

const fallbackDefaultWidth = 320;

export const onPeekStartDelayMs = 500;

/**
 * We need an additional component layer so we can wrap the side nav in a `OpenLayerObserver` and have access to the
 * context value.
 */
function SideNavInternal({
	children,
	defaultCollapsed,
	defaultWidth: defaultWidthProp = fallbackDefaultWidth,
	minWidth = fallbackMinWidth,
	maxWidth,
	testId,
	label = 'Sidebar',
	skipLinkLabel = label,
	onExpand,
	onCollapse,
	onPeekStart,
	onPeekEnd,
	id: providedId,
	canToggleWithShortcut,
}: SideNavProps) {
	const isChatPanelLayoutEnabled = fg('platform-dst-chat-panel-layout');
	const id = useLayoutId({ providedId });
	const expandAndFocusSideNav = useExpandSideNav({ trigger: 'skip-link' });
	/**
	 * Called after clicking on the side nav skip link, and ensures the side nav is expanded so that it is focusable.
	 *
	 * We need to update the DOM synchronously because `.focus()` is called synchronously after this state update.
	 *
	 * Only used when `platform_dst_nav4_skip_link_a11y_1` is OFF; can be removed on gate cleanup.
	 */
	const synchronouslyExpandSideNav = useCallback(() => {
		flushSync(() => {
			/**
			 * Calling this unconditionally and relying on it to avoid no-op renders.
			 *
			 * We _could_ call it conditionally, but we'd be duplicating the screen size checks `expandAndFocusSideNav` makes.
			 */
			expandAndFocusSideNav();
		});
	}, [expandAndFocusSideNav]);

	useSkipLinkInternal({
		id,
		label: skipLinkLabel,
		/**
		 * `navigate` is the gate-on contract: it owns expanding the side nav AND moving
		 * focus to the first nav item, atomically (via `useExpandSideNav`'s `flushSync`).
		 *
		 * `onBeforeNavigate` is the legacy contract used only when the gate is OFF.
		 * On gate cleanup, drop `onBeforeNavigate` and `synchronouslyExpandSideNav` here.
		 */
		navigate: fg('platform_dst_nav4_skip_link_a11y_1') ? expandAndFocusSideNav : undefined,
		onBeforeNavigate: fg('platform_dst_nav4_skip_link_a11y_1')
			? undefined
			: synchronouslyExpandSideNav,
	});

	const sideNavState = useContext(SideNavVisibilityState);
	const setSideNavState = useContext(SetSideNavVisibilityState);
	const { isExpandedOnDesktop, isExpandedOnMobile } = useSideNavVisibility({
		defaultCollapsed,
	});
	// We are placing `defaultCollapsed` into a state container so we can have a stable reference to the initial value.
	// This is so we can use it in an effect _that only runs once_, after the initial render on the client,
	// to sync the side nav context (provided in `<Root>`) with the `defaultCollapsed` prop provided to `<SideNav>`.
	const [initialDefaultCollapsed] = useState(defaultCollapsed);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [initialIsExpandedOnDesktop] = useState(isExpandedOnDesktop);

	/**
	 * Captures the initial collapsed/expanded state of the side nav.
	 *
	 * Only firing on desktop because the nav is never open by default on mobile.
	 */
	useEffect(() => {
		if (initialIsExpandedOnDesktop) {
			const isDesktop = window.matchMedia('(min-width: 64rem)').matches;
			if (isDesktop) {
				const navigationAnalyticsEvent = createAnalyticsEvent({
					source: 'topNav',
					actionSubject: 'sideNav',
					action: 'viewedOnLoad',
					actionSubjectId: 'sideNavMenu',
					attributes: {
						screen: 'desktop',
					},
				});

				navigationAnalyticsEvent.fire('navigation');
			}
		}
	}, [createAnalyticsEvent, initialIsExpandedOnDesktop]);

	const defaultWidth = useSafeDefaultWidth({
		defaultWidthProp,
		fallbackDefaultWidth,
		slotName: 'SideNav',
	});

	const [inlineWidth, setInlineWidth] = useState(defaultWidth);
	const [overlayWidth, setOverlayWidth] = useState(defaultWidth);
	const [isInline, setIsInline] = useState(false);
	useEffect(() => {
		if (!isChatPanelLayoutEnabled) {
			return;
		}
		const mediaQuery = window.matchMedia('(min-width: 64rem)');
		setIsInline(mediaQuery.matches);
		const onChange = (event: MediaQueryListEvent) => setIsInline(event.matches);
		return bind(mediaQuery, { type: 'change', listener: onChange });
	}, [isChatPanelLayoutEnabled]);
	const toggleVisibilityForChatPanel = useToggleSideNav({ trigger: 'programmatic' });
	const closeOverlayForChatPanel = useCallback(() => {
		// Allocation can overlay an expanded desktop nav when other slots consume
		// its space. Dismiss the visibility state selected by the viewport, not the mode.
		const isDesktop = window.matchMedia('(min-width: 64rem)').matches;
		if (isDesktop ? isExpandedOnDesktop : isExpandedOnMobile) {
			toggleVisibilityForChatPanel();
		}
	}, [isExpandedOnDesktop, isExpandedOnMobile, toggleVisibilityForChatPanel]);
	const {
		state: managedSizing,
		isManaged,
		prepareResize,
		getResizeBounds: getManagedResizeBounds,
		startResize,
		resize,
		completeResize,
	} = useLayoutAreaSizing({
		area: 'side-nav',
		config: {
			defaultWidth,
			minWidth,
			maxWidth,
			isOpen: isInline ? isExpandedOnDesktop : isExpandedOnMobile,
			onRequestClose: closeOverlayForChatPanel,
		},
	});
	const mode = managedSizing?.mode ?? (isInline ? 'inline' : 'overlay');
	const renderedWidth = isChatPanelLayoutEnabled
		? (managedSizing?.width ?? (mode === 'inline' ? inlineWidth : overlayWidth))
		: inlineWidth;
	const configuredMaxWidth = maxWidth ?? '100vw';
	const clampedWidth = !isChatPanelLayoutEnabled
		? `clamp(240px, ${inlineWidth}px, 50vw)`
		: managedSizing
			? `${renderedWidth}px`
			: `clamp(${minWidth}, ${renderedWidth}px, ${configuredMaxWidth})`;
	const dangerouslyHoistSlotSizes = useContext(DangerouslyHoistSlotSizes);

	const navRef = useRef<HTMLDivElement | null>(null);
	const getResizeBounds = useCallback((): ResizeBounds => {
		if (!isChatPanelLayoutEnabled) {
			return { min: '240px', max: '50vw' };
		}
		const managedBounds = getManagedResizeBounds();
		if (managedBounds) {
			return managedBounds;
		}
		const sideNav = navRef.current;
		const layoutRoot = sideNav?.parentElement;
		const main = layoutRoot?.querySelector<HTMLElement>(':scope > [data-layout-slot][role="main"]');
		const fallbackMaxWidth = maxWidth ?? (mode === 'inline' ? '100vw' : '90vw');

		if (!sideNav || mode === 'overlay') {
			const chatPanel = layoutRoot?.querySelector<HTMLElement>(':scope > [data-layout-chat-panel]');
			const inlineChatPanelWidth =
				chatPanel && getComputedStyle(chatPanel).gridArea === 'chat-panel'
					? chatPanel.getBoundingClientRect().width
					: 0;
			const overlayMaxWidth = Math.floor((window.innerWidth - inlineChatPanelWidth) * 0.9);
			const constrainedMaxWidth = maxWidth
				? Math.min(convertResizeBoundToPixels(maxWidth), overlayMaxWidth)
				: overlayMaxWidth;
			return {
				min: minWidth,
				max: `${Math.max(convertResizeBoundToPixels(minWidth), constrainedMaxWidth)}px`,
			};
		}

		if (!main) {
			return { min: minWidth, max: fallbackMaxWidth };
		}

		const sideNavWidth = sideNav.getBoundingClientRect().width;
		const mainWidth = main.getBoundingClientRect().width;

		// JSDOM does not perform layout, so retain the static fallback in unit tests and other
		// environments where used widths cannot be measured.
		if (sideNavWidth === 0 || mainWidth === 0) {
			return { min: minWidth, max: fallbackMaxWidth };
		}

		const localPanel = main.querySelector<HTMLElement>('[data-layout-with-panel-slot]');
		const isLocalPanelInline = localPanel && getComputedStyle(localPanel).gridArea === 'panel';
		const localPanelWidth = isLocalPanelInline ? localPanel.getBoundingClientRect().width : 0;
		const mainContentWidth = mainWidth - localPanelWidth;
		const mainMinimumWidth = getPixelCustomProperty(main, mainMinimumWidthVar, defaultMainMinWidth);
		const availableWidth = Math.max(0, mainContentWidth - mainMinimumWidth);
		const availableInlineWidth = Math.floor(sideNavWidth + availableWidth);
		const constrainedMaxWidth = maxWidth
			? Math.min(convertResizeBoundToPixels(maxWidth), availableInlineWidth)
			: availableInlineWidth;

		return {
			min: minWidth,
			max: `${Math.max(convertResizeBoundToPixels(minWidth), constrainedMaxWidth)}px`,
		};
	}, [getManagedResizeBounds, isChatPanelLayoutEnabled, maxWidth, minWidth, mode]);
	/**
	 * Used to share the side nav element with the `Panel`,
	 * which observes the side nav to determine its maximum width.
	 */
	const sharedRef = useSideNavRef();
	const mergedRef = mergeRefs([navRef, sharedRef]);
	const onCompleteResize = useCallback(
		(finalWidth: number) => {
			if (!isChatPanelLayoutEnabled) {
				setInlineWidth(finalWidth);
				return;
			}
			if (mode === 'inline') {
				setInlineWidth(finalWidth);
			} else {
				setOverlayWidth(finalWidth);
			}
			completeResize(mode, finalWidth);
		},
		[completeResize, isChatPanelLayoutEnabled, mode],
	);
	const onResizeStartInternal = useCallback(() => {
		startResize(mode);
	}, [mode, startResize]);
	const onResizeInternal = useCallback((width: number) => resize(mode, width), [mode, resize]);

	const toggleButtonElement = useContext(SideNavToggleButtonElement);
	const topNavStartElement = useContext(TopNavStartElement);
	const devTimeOnlyAttributes: Record<string, string | boolean> = {};
	const openLayerObserver = useOpenLayerObserver({ isEnabled: !fg('platform-dst-top-layer') });
	const flyoutStateRef = useRef<FlyoutState>({ type: 'not-active' });
	const isFlyoutVisible = sideNavState?.flyout === 'open';
	// Flyouts, including their close animation, already have overlay placement.
	// The CSS-first inline allocation must not override it with an ID selector.
	const shouldApplyUnmanagedSizing =
		!managedSizing &&
		sideNavState?.flyout !== 'open' &&
		sideNavState?.flyout !== 'triggered-animate-close';

	const isExpandedOnDesktopRef = useStableRef(isExpandedOnDesktop);
	const hasPeekStartedRef = useRef(false);
	const onPeekStartRef = useStableRef(onPeekStart);
	const onPeekEndRef = useStableRef(onPeekEnd);

	const onPeekStartTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

	useEffect(() => {
		return () => {
			clearTimeout(onPeekStartTimeoutIdRef.current);
		};
	}, []);

	const updateFlyoutState = useMemo(() => {
		function tryAbortPendingClose() {
			if (flyoutStateRef.current.type === 'waiting-for-close') {
				flyoutStateRef.current.abort();
			}
		}

		function open() {
			const prevFlyoutState = flyoutStateRef.current;

			tryAbortPendingClose();
			flyoutStateRef.current = { type: 'open' };
			setSideNavState((currentState) => {
				if (currentState?.desktop === 'collapsed' && currentState?.flyout !== 'open') {
					return {
						...currentState,
						flyout: 'open',
					};
				}

				return currentState;
			});

			// Avoid redundant calls to `onPeekStart()`
			if (prevFlyoutState.type === 'not-active') {
				clearTimeout(onPeekStartTimeoutIdRef.current);
				onPeekStartTimeoutIdRef.current = setTimeout(() => {
					// If the flyout isn't still open after ~500ms then we won't count the peek
					// As we want to track user intention rather than all hovers
					if (isExpandedOnDesktopRef.current || flyoutStateRef.current.type !== 'open') {
						return;
					}

					hasPeekStartedRef.current = true;
					onPeekStartRef.current?.();
				}, onPeekStartDelayMs);
			}
		}

		function close() {
			const prevFlyoutState = flyoutStateRef.current;

			tryAbortPendingClose();
			flyoutStateRef.current = { type: 'not-active' };
			setSideNavState((currentState) => {
				if (currentState?.desktop === 'collapsed' && currentState?.flyout === 'open') {
					return {
						...currentState,
						flyout: 'triggered-animate-close',
					};
				}

				return currentState;
			});

			// Avoid redundant calls to `onPeekEnd()`
			if (prevFlyoutState.type !== 'not-active' && hasPeekStartedRef.current) {
				hasPeekStartedRef.current = false;
				onPeekEndRef.current?.({
					trigger: isExpandedOnDesktopRef.current ? 'side-nav-expand' : 'mouse-leave',
				});
			}
		}

		function canClose(): boolean {
			if (!fg('platform-dst-top-layer')) {
				return openLayerObserver?.getCount({ namespace: openLayerObserverSideNavNamespace }) === 0;
			}
			const nav = navRef.current;
			return !(nav && isFlyoutHeldOpen({ nav }));
		}

		return function onAction(
			action:
				| 'open'
				| 'drag-from-flyout-started'
				| 'drag-from-flyout-finished'
				| 'waiting-for-close'
				| 'ready-to-close'
				| 'force-close',
		) {
			if (action === 'drag-from-flyout-started') {
				open();
				flyoutStateRef.current = { type: 'is-dragging-from-flyout' };
				return;
			}

			if (action === 'drag-from-flyout-finished') {
				open();
				return;
			}

			// ignoring all actions until the drag is finished
			if (flyoutStateRef.current.type === 'is-dragging-from-flyout') {
				return;
			}

			if (action === 'open') {
				open();
				return;
			}

			if (action === 'waiting-for-close') {
				if (flyoutStateRef.current.type === 'waiting-for-close') {
					return;
				}

				// A timeout is used to close the flyout after a delay when the user mouses out of the flyout area, and to allow
				// us to cancel the close if the user mouses back in.
				const timeout = setTimeout(() => {
					updateFlyoutState('ready-to-close');
				}, sideNavFlyoutCloseDelayMs);

				flyoutStateRef.current = {
					type: 'waiting-for-close',
					abort() {
						clearTimeout(timeout);
					},
				};

				return;
			}

			if (action === 'ready-to-close') {
				// If there are no open layers, we can close the flyout.
				if (canClose()) {
					close();
					return;
				}

				flyoutStateRef.current = { type: 'ready-to-close' };
				return;
			}

			if (action === 'force-close') {
				close();
				return;
			}
		};
	}, [isExpandedOnDesktopRef, onPeekEndRef, onPeekStartRef, openLayerObserver, setSideNavState]);

	const toggleVisibilityByScreenResize = useToggleSideNav({ trigger: 'screen-resize' });
	const toggleVisibilityByClickOutsideOnMobile = useToggleSideNav({
		trigger: 'click-outside-on-mobile',
	});

	// Using `useLayoutEffect` so that there's no visual shift in non-SSR environments when using legacy API
	// For SSR the new API is still necessary
	useLayoutEffect(() => {
		if (sideNavState !== null) {
			// Only need to do an initial sync if it hasn't been initialized from Root
			return;
		}

		// Sync the visibility in context (provided in `<Root>`) with the local `defaultCollapsed` prop provided to `SideNav`
		// after SSR hydration. This should only run once, after the initial render on the client.
		startTransition(() => {
			setSideNavState({
				desktop: initialDefaultCollapsed ? 'collapsed' : 'expanded',
				mobile: 'collapsed',
				flyout: 'closed',
				lastTrigger: null,
			});
		});
	}, [initialDefaultCollapsed, setSideNavState, sideNavState]);

	const handleExpand = useCallback<VisibilityCallback>(
		({ screen, trigger }) => {
			onExpand?.({ screen, trigger });

			const navigationAnalyticsEvent = createAnalyticsEvent({
				source: 'topNav',
				actionSubject: 'sideNav',
				action: 'expanded',
				actionSubjectId: 'sideNavMenu',
				attributes: {
					trigger,
				},
			});

			navigationAnalyticsEvent.fire('navigation');

			// When the side nav gets expanded, we close the flyout to reset it.
			// This prevents the flyout from staying open and ensures we are respecting the user's intent to expand.
			updateFlyoutState('force-close');
		},
		[onExpand, updateFlyoutState, createAnalyticsEvent],
	);

	const handleCollapse = useCallback<VisibilityCallback>(
		({ screen, trigger }) => {
			onCollapse?.({ screen, trigger });

			const navigationAnalyticsEvent = createAnalyticsEvent({
				source: 'topNav',
				actionSubject: 'sideNav',
				action: 'collapsed',
				actionSubjectId: 'sideNavMenu',
				attributes: {
					trigger,
				},
			});

			navigationAnalyticsEvent.fire('navigation');

			// When the side nav gets collapsed, we close the flyout to reset it.
			// This prevents the flyout from staying open and ensures we are respecting the user's intent to collapse.
			updateFlyoutState('force-close');
		},
		[onCollapse, updateFlyoutState, createAnalyticsEvent],
	);

	useSideNavVisibilityCallbacks({
		onExpand: handleExpand,
		onCollapse: handleCollapse,
		isExpandedOnDesktop,
		isExpandedOnMobile,
		lastTrigger: sideNavState?.lastTrigger ?? null,
	});

	useEffect(() => {
		const mediaQueryList = window.matchMedia('(min-width: 64rem)');
		return bind(mediaQueryList, {
			type: 'change',
			listener() {
				if (mediaQueryList.matches) {
					// We're transitioning from tablet to desktop viewport size.
					// We forcibly show the side nav if it was shown on mobile.
					if (isExpandedOnMobile && !isExpandedOnDesktop) {
						toggleVisibilityByScreenResize();
					}
				}
			},
		});
	}, [toggleVisibilityByScreenResize, isExpandedOnDesktop, isExpandedOnMobile]);

	/**
	 * Close the mobile side nav if there is a click outside.
	 *
	 * Binding/unbinding on changes to `isExpandedOnMobile` instead of window size changes for simplicity.
	 * If we listened to window size changes we'd need nested `bind()` calls.
	 */
	useEffect(() => {
		if (!isExpandedOnMobile) {
			return;
		}

		/**
		 * The expected URL fragment used by the side nav skip link.
		 */
		const skipLinkUrlFragment = `#${id}`;

		return bind(window, {
			type: 'click',
			listener(event) {
				if (window.matchMedia('(min-width: 64rem)').matches) {
					// Clicks on desktop should do nothing
					return;
				}

				const sideNav = navRef.current;
				if (!sideNav || !(event.target instanceof Node)) {
					// Ignore if our element references are invalid
					return;
				}

				if (!event.target.isConnected) {
					/**
					 * If the element that was clicked is no longer in the document, we should not collapse the side nav.
					 * This can happen when the user clicks on a dropdown menu item (such as one used in a `...` More menu),
					 * as the dropdown menu will close after being clicked. By the time this event listener runs, the clicked
					 * element will no longer be in the document, so the check below for `sideNav.contains(event.target)` will fail.
					 */
					return;
				}

				if (sideNav.contains(event.target)) {
					// Clicks inside the side nav should not close it
					return;
				}

				if (toggleButtonElement?.contains(event.target)) {
					// Clicks on the toggle button will already toggle the side nav,
					// so we need to ignore it or we end up doing a double toggle
					return;
				}

				if (
					event.target instanceof HTMLAnchorElement &&
					/**
					 * Intentionally using `.getAttribute()` for simplicity to match exactly what we expect
					 * for skip links.
					 *
					 * Alternatives considered:
					 *
					 * - Using `.href` returns a full URL, which would then require extra parsing.
					 * - Using `.hash` could incorrectly capture `<a href="/another-page#skip-link-id>` so we'd need extra checks.
					 *
					 * This approach has an edge case of `<a href="/same-page#skip-link-id>` not being captured,
					 * but we are okay with that trade-off.
					 */
					event.target.getAttribute('href') === skipLinkUrlFragment
				) {
					// This was a click on a link to the side nav, so we shouldn't try to collapse the SideNav.
					// It was most likely the skip link, although anyone can render a link with the ID.
					// In either case keeping the SideNav expanded makes sense.
					return;
				}

				toggleVisibilityByClickOutsideOnMobile();
			},
		});
	}, [id, isExpandedOnMobile, toggleButtonElement, toggleVisibilityByClickOutsideOnMobile]);

	useEffect(() => {
		if (!toggleButtonElement) {
			return;
		}

		return bind(toggleButtonElement, {
			type: 'mouseenter',
			listener() {
				// Only flyout the side nav if the user hovered while the side nav was collapsed
				if (isExpandedOnDesktop) {
					return;
				}

				// Only flyout the side nav on desktop viewports
				const { matches } = window.matchMedia('(min-width: 64rem)');
				if (matches) {
					updateFlyoutState('open');
				}
			},
		});
	}, [updateFlyoutState, toggleButtonElement, isExpandedOnDesktop]);

	useEffect(() => {
		if (!toggleButtonElement) {
			return;
		}

		return bind(toggleButtonElement, {
			type: 'mouseleave',
			listener() {
				// If the side nav is not in flyout mode, we don't need to do anything
				if (!isFlyoutVisible) {
					return;
				}

				updateFlyoutState('waiting-for-close');
			},
		});
	}, [isFlyoutVisible, toggleButtonElement, updateFlyoutState]);

	useEffect(() => {
		if (!navRef.current) {
			return;
		}

		return bind(navRef.current, {
			type: 'mouseenter',
			listener() {
				// If the side nav is not in flyout mode, we don't need to do anything
				if (isExpandedOnDesktop || !isFlyoutVisible) {
					return;
				}

				// The user mouses has moused back over the side nav
				updateFlyoutState('open');
			},
		});
	}, [isFlyoutVisible, updateFlyoutState, isExpandedOnDesktop]);

	useEffect(() => {
		if (!navRef.current) {
			return;
		}

		return bind(navRef.current, {
			type: 'mouseleave',
			listener() {
				// If the side nav is not in flyout mode, we don't need to do anything
				if (!isFlyoutVisible) {
					return;
				}

				updateFlyoutState('waiting-for-close');
			},
		});
	}, [isFlyoutVisible, updateFlyoutState]);

	useEffect(() => {
		const nav = navRef.current;

		if (!nav) {
			return;
		}

		if (!isFlyoutVisible) {
			return;
		}

		// Using a monitor rather than a drop target. Rationale:
		// - We are not creating a drop target for the users to drop on,
		//   we are just interested in listening to events within an element
		// - We do not want to impact `location.{*}.dropTargets` in events
		return monitorForElements({
			canMonitor({ source }) {
				return nav.contains(source.element);
			},
			onGenerateDragPreview() {
				updateFlyoutState('drag-from-flyout-started');
			},
			onDrop({ location }) {
				// Always marking the drag and done
				updateFlyoutState('drag-from-flyout-finished');

				// If the user dropped outside of the flyout, we will close the flyout
				const underUsersPointer = document.elementFromPoint(
					location.current.input.clientX,
					location.current.input.clientY,
				);

				if (!nav.contains(underUsersPointer)) {
					updateFlyoutState('waiting-for-close');
				}
			},
		});
	}, [isFlyoutVisible, updateFlyoutState]);

	useEffect(() => {
		if (!topNavStartElement || !toggleButtonElement) {
			return;
		}

		return bind(topNavStartElement, {
			type: 'mouseover',
			listener(event) {
				// If the side nav is not in flyout mode, we don't need to do anything
				if (isExpandedOnDesktop || !isFlyoutVisible) {
					return;
				}

				if (event.target === topNavStartElement) {
					// The mouse is directly over the TopNavStart element, so cancel any pending flyout closes.
					updateFlyoutState('open');
					return;
				}

				if (event.target instanceof Element && toggleButtonElement.contains(event.target)) {
					// The mouse is over the toggle button or any of its children, so we don't want to close the flyout.
					// We also don't need to cancel any pending closes, as we have separate event listeners for the toggle button mouse events.
					return;
				}

				// The user has moused over a child element of the TopNavStart element that isn't the toggle button, e.g. the app switcher or nav logo,
				// so we should close the flyout (with a delay).
				updateFlyoutState('waiting-for-close');
			},
		});
	}, [
		topNavStartElement,
		isFlyoutVisible,
		toggleButtonElement,
		isExpandedOnDesktop,
		updateFlyoutState,
	]);

	useEffect(() => {
		if (!topNavStartElement) {
			return;
		}

		return bind(topNavStartElement, {
			type: 'mouseleave',
			listener() {
				// If the side nav is not in flyout mode, we don't need to do anything
				if (!isFlyoutVisible) {
					return;
				}

				// The mouse has left the TopNavStart element, so we should close the flyout with a delay.
				updateFlyoutState('waiting-for-close');
			},
		});
	}, [topNavStartElement, isFlyoutVisible, updateFlyoutState]);

	useEffect(() => {
		// Close the flyout if there are no more layers open and the user is not mousing over the
		// flyout areas (side nav, TopNavStart element)

		return openLayerObserver?.onChange(
			({ count }) => {
				if (flyoutStateRef.current.type === 'ready-to-close' && count === 0) {
					updateFlyoutState('force-close');
				}
			},
			{ namespace: openLayerObserverSideNavNamespace },
		);
	}, [openLayerObserver, updateFlyoutState]);

	const focusOutTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

	useEffect(() => {
		// The same as the open layer observer `onChange` above, for when the `platform-dst-top-layer`
		// gate is on. Top-layer surfaces opened from the side nav are DOM descendants of it.
		const nav = navRef.current;
		if (!nav || !fg('platform-dst-top-layer')) {
			return;
		}

		function closeIfReady() {
			if (flyoutStateRef.current.type !== 'ready-to-close' || !nav) {
				return;
			}
			if (isFlyoutHeldOpen({ nav })) {
				return;
			}
			updateFlyoutState('force-close');
		}

		const unbindLayerChange = bindOpenLayerChange({ within: nav, onChange: closeIfReady });
		const unbindFocusOut = bind(nav, {
			type: 'focusout',
			listener() {
				// Wait for focus to move before checking where it went.
				clearTimeout(focusOutTimeoutIdRef.current);
				focusOutTimeoutIdRef.current = setTimeout(closeIfReady, 0);
			},
		});

		// Removing the focused text field (for example when an inline rename ends) fires no
		// `focusout` in some browsers, so also check again when content inside the side nav changes.
		const removalObserver = new MutationObserver(closeIfReady);
		removalObserver.observe(nav, { childList: true, subtree: true });

		return function cleanup() {
			unbindLayerChange();
			unbindFocusOut();
			removalObserver.disconnect();
			clearTimeout(focusOutTimeoutIdRef.current);
		};
	}, [updateFlyoutState]);

	useEffect(() => {
		// Clear flyout close timer if being unmounted
		return function cleanupPendingFlyoutClose() {
			if (flyoutStateRef.current.type === 'waiting-for-close') {
				flyoutStateRef.current.abort();
			}
		};
	}, []);

	if (process.env.NODE_ENV !== 'production') {
		const visible: string[] = [];
		if (isExpandedOnMobile) {
			visible.push('small');
		}
		if (isExpandedOnDesktop) {
			visible.push('large');
		}
		if (isFlyoutVisible) {
			visible.push('flyout');
		}
		devTimeOnlyAttributes['data-visible'] = visible.length ? visible.join(',') : 'false';
	}

	useSideNavToggleKeyboardShortcut({ canToggleWithShortcut });
	// Used to conditionally display the keyboard shortcut in the SideNavPanelSplitter tooltip.
	const isShortcutEnabled = useIsSideNavShortcutEnabled();

	useResizingWidthCssVarOnRootElement({
		isEnabled: !isChatPanelLayoutEnabled || !managedSizing,
		cssVar: panelSplitterResizingVar,
		panelId: sideNavPanelSplitterId,
	});

	const isExpandedStateDifferentFromInitial =
		isExpandedOnMobile || isExpandedOnDesktop !== initialIsExpandedOnDesktop;

	/**
	 * Used to track (between renders) if the expanded state of the side nav has ever changed since mount.
	 *
	 * Cannot rely solely on `isExpandedStateDifferentFromInitial` because the expanded state can go back to the initial state.
	 */
	const hasExpandedStateChangedRef = useRef(false);

	useEffect(() => {
		if (hasExpandedStateChangedRef.current) {
			return;
		}

		if (isExpandedStateDifferentFromInitial) {
			hasExpandedStateChangedRef.current = true;
		}
	}, [isExpandedStateDifferentFromInitial]);

	/**
	 * If the expanded state of the side nav has ever changed since mount.
	 *
	 * Used to prevent sidebar expand animations from running on the initial load.
	 * Otherwise, the `@starting-style` rule will cause the sidebar to slide in initially.
	 */
	const hasExpandedStateChanged =
		isExpandedStateDifferentFromInitial || hasExpandedStateChangedRef.current;

	const isMotionUpliftEnabled = fg('platform-dst-motion-uplift-sidenav');

	// This is only used for the regular expand and collapse animations, not the flyout animations.
	const shouldShowSidebarToggleAnimation =
		isMotionUpliftEnabled &&
		// We do not apply the animation styles on the initial render, as the `@starting-style` rule will cause the sidebar to
		// slide in initially.
		hasExpandedStateChanged &&
		// We also do not apply the animation styles if the side nav is in flyout mode to make sure we don't override flyout styles.
		// If we instead try to unset the `transform` property in the flyout styles using `transform: initial`, it results in the entire
		// flyout animation being disabled.
		!isFlyoutVisible &&
		/**
		 * Disabling animations for Firefox, as it doesn't support animating the `display` property:
		 * https://caniuse.com/mdn-css_properties_display_is_transitionable
		 *
		 * As of October 2025, Firefox supports the expand animation, but not the collapse animation (it instantly changes to `display: none`).
		 * Because of this, we are disabling all sidebar animations for Firefox so it doesn't look like a bug.
		 *
		 * Unfortunately we cannot use `@supports` to target the support of `transition-behavior: allow-discrete` specifically
		 * for the `display` property. And `@supports at-rule(@starting-style)` is also not ready for browser use yet.
		 *
		 * We are using JS to detect Firefox and disable animations, instead of using CSS, as Compiled currently does not merge duplicate
		 * CSS at-rules when at-rules are nested: https://github.com/atlassian-labs/compiled/blob/e04a325915e1d13010205089e4915de0e53bc2d4/packages/css/src/plugins/merge-duplicate-at-rules.ts#L5
		 * Avoiding nesting the `@supports` at-rule inside of `@media` means Compiled can remove duplicate styles from the generated CSS.
		 */
		!isFirefox;

	return (
		<React.Fragment>
			<nav
				id={id}
				{...devTimeOnlyAttributes}
				data-layout-slot
				aria-label={label}
				style={
					{
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/enforce-style-prop
						[sideNavVar]: clampedWidth,
					} as CSSProperties
				}
				ref={mergedRef}
				css={[
					styles.root,
					isChatPanelLayoutEnabled && styles.chatPanelSizing,
					isChatPanelLayoutEnabled && managedSizing?.mode === 'overlay' && styles.managedOverlay,
					// We are explicitly using the `isExpandedOnDesktop` and `isExpandedOnMobile` values here to ensure we are displaying the
					// correct state during SSR render, as the context value would not have been set yet. These values are derived from the
					// component props (defaultCollapsed) if context hasn't been set yet.
					isExpandedOnDesktop && !isExpandedOnMobile && !isFlyoutVisible && styles.hiddenMobileOnly,
					!isExpandedOnDesktop &&
						isExpandedOnMobile &&
						!isFlyoutVisible &&
						styles.hiddenDesktopOnly,
					!isExpandedOnDesktop &&
						!isExpandedOnMobile &&
						!isFlyoutVisible &&
						styles.hiddenMobileAndDesktop,

					isMotionUpliftEnabled && motionUpliftStyles.animationRTLSupport,
					// Expand/collapse animation styles
					shouldShowSidebarToggleAnimation &&
						isMotionUpliftEnabled &&
						motionUpliftStyles.animationBaseStyles,
					// We need to separately apply the styles for the expand or collapse animations for both mobile and desktop
					// based on their relevant expansion state.
					isExpandedOnMobile &&
						shouldShowSidebarToggleAnimation &&
						isMotionUpliftEnabled &&
						motionUpliftStyles.expandAnimationMobile,
					!isExpandedOnMobile &&
						shouldShowSidebarToggleAnimation &&
						isMotionUpliftEnabled &&
						motionUpliftStyles.collapseAnimationMobile,
					isExpandedOnDesktop &&
						shouldShowSidebarToggleAnimation &&
						isMotionUpliftEnabled &&
						motionUpliftStyles.expandAnimationDesktop,
					!isExpandedOnDesktop &&
						shouldShowSidebarToggleAnimation &&
						isMotionUpliftEnabled &&
						motionUpliftStyles.collapseAnimationDesktop,

					// Flyout styles
					sideNavState?.flyout === 'open' && isMotionUpliftEnabled && motionUpliftStyles.flyoutOpen,
					sideNavState?.flyout === 'open' && !isMotionUpliftEnabled && styles.flyoutOpen,
					sideNavState?.flyout === 'triggered-animate-close' &&
						isMotionUpliftEnabled &&
						motionUpliftStyles.flyoutAnimateClosed,
					sideNavState?.flyout === 'triggered-animate-close' &&
						!isMotionUpliftEnabled &&
						styles.flyoutAnimateClosed,
				]}
				data-testid={testId}
			>
				{isChatPanelLayoutEnabled && (
					// CSS reserves only the nav minimum for chat's fit decision, before effects or lazy loading.
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles
					<style>{`
#${gridRootChatFitId} { --n_fitNav: 0px; }
${shouldApplyUnmanagedSizing ? `#${gridRootId} nav[data-layout-slot] { grid-area: main / aside / aside / aside; width: min(90%, ${clampedWidth}); }` : ''}
@media (min-width: 64rem) {
  @container navigation-main-space (min-width: ${minWidth}) {
    #${gridRootChatFitId} { --n_fitNav: ${isExpandedOnDesktop ? minWidth : '0px'}; }
    ${shouldApplyUnmanagedSizing ? `#${gridRootId} nav[data-layout-slot] { grid-area: side-nav; width: min(${clampedWidth}, calc(100cqw + var(--n_fitNav) - var(--n_chatTrack, 0px))); }` : ''}
  }
}
`}</style>
				)}
				{/**
				 * This CSS var is used by the `Panel` slot to enforce its maximum width constraint.
				 * When we remove the UNSAFE legacy usage, we can change this to `HoistCssVarToLocalGrid`
				 */}
				<DangerouslyHoistCssVarToDocumentRoot
					variableName={sideNavLiveWidthVar}
					value="0px"
					mediaQuery={'@media (min-width: 64rem)' satisfies MediaAboveMd}
					responsiveValue={
						isExpandedOnDesktop ? `var(${panelSplitterResizingVar}, ${clampedWidth})` : 0
					}
				/>
				{dangerouslyHoistSlotSizes && (
					// ------ START UNSAFE STYLES ------
					// These styles are only needed for the UNSAFE legacy use case for Jira + Confluence.
					// When they aren't needed anymore we can delete them wholesale.
					<DangerouslyHoistCssVarToDocumentRoot
						variableName={UNSAFE_sideNavLayoutVar}
						value={`var(${sideNavLiveWidthVar})`}
					/>
					// ------ END UNSAFE STYLES ------
				)}
				<PanelSplitterProvider
					panelId={sideNavPanelSplitterId}
					panelRef={navRef}
					panelWidth={renderedWidth}
					onCompleteResize={onCompleteResize}
					onResizeStartInternal={
						isChatPanelLayoutEnabled && isManaged ? onResizeStartInternal : undefined
					}
					onResizeInternal={isChatPanelLayoutEnabled && isManaged ? onResizeInternal : undefined}
					prepareResize={isChatPanelLayoutEnabled ? prepareResize : undefined}
					getResizeBounds={getResizeBounds}
					resizingCssVar={panelSplitterResizingVar}
					// Not resizable when in peek (flyout) mode.
					isEnabled={!isFlyoutVisible}
					shortcut={isShortcutEnabled ? sideNavToggleTooltipKeyboardShortcut : undefined}
				>
					<div css={styles.flexContainer}>{children}</div>
				</PanelSplitterProvider>
			</nav>
		</React.Fragment>
	);
}

/**
 * The side navigation layout area. It will show on the left (inline start) of the screen.
 *
 * Use the side nav area components (`SideNavHeader`, `SideNavBody`, `SideNavFooter`) to position
 * content within areas of the side nav.
 *
 * You can optionally render a `PanelSplitter` as a child to make the side navigation slot resizable.
 */
export function SideNav({
	children,
	defaultCollapsed,
	defaultWidth = 320,
	minWidth,
	maxWidth,
	testId,
	label, // Default value is defined in `SideNavInternal`
	skipLinkLabel = label, // Default value is defined in `SideNavInternal`
	onExpand,
	onCollapse,
	onPeekStart,
	onPeekEnd,
	canToggleWithShortcut,
	id,
}: SideNavProps): JSX.Element {
	const sideNav = (
		<SideNavInternal
			defaultCollapsed={defaultCollapsed}
			defaultWidth={defaultWidth}
			minWidth={minWidth}
			maxWidth={maxWidth}
			testId={testId}
			label={label}
			skipLinkLabel={skipLinkLabel}
			onExpand={onExpand}
			onCollapse={onCollapse}
			onPeekStart={onPeekStart}
			onPeekEnd={onPeekEnd}
			id={id}
			canToggleWithShortcut={canToggleWithShortcut}
		>
			{children}
		</SideNavInternal>
	);

	// Top-layer surfaces are found through the DOM, so the namespace is only needed when the gate is off.
	if (fg('platform-dst-top-layer')) {
		return sideNav;
	}

	return (
		<OpenLayerObserverNamespaceProvider namespace={openLayerObserverSideNavNamespace}>
			{sideNav}
		</OpenLayerObserverNamespaceProvider>
	);
}
